import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  CircularProgress,
  Container,
  Stack,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  getLocalStorageItem,
  groupBy,
  repairRequestDbStatuses,
  setLocalStorageItem,
} from "../../_helpers";
import { getRRDashboardCounts } from "../../apis/surveyingApis";
import { apps, LOCAL_STORAGE_GRID_KEYS, userAccountRoleConstants } from "../../_constants";
import DataGridV2 from "../../components/DataGridV2";
import { LabelColumnV2, LinkColumnV2 } from "../../components/DataGridV2/DataColumnsV2";
import { getGridContextMenuList } from "../../_helpers/getGridContextMenuList";
import { INITIAL_ROW_SELECTION_MODEL } from "../../components/DataGridV2/constants";
import { useGridApiRef } from "@mui/x-data-grid-pro";
import { Add } from "@mui/icons-material";
import { getIdsForGridSelectedRows } from "../../_helpers/getIdsForGridSelectedRows";
import QuestionnaireCreationModal from "../Questionnaire/QuestionnaireCreation/QuestionnaireCreationModal";

const Dashboard = (props) => {
  const { history } = props;
  const { t, i18n, ready } = useTranslation();
  const [rowsData, setRowsData] = useState([]);
  const classes = useStyles();
  const { all = [], loading: buildingLoading } = useSelector(
    (state) => state.buildings
  );
   
  const app = useSelector((state) => state.app);
  const { lng } = useSelector((state) => state.appConfig) || {}
  const keyPrefix = app === apps.aftercare ? "AFTERCARE" :  app === apps.constructionQuality ? "CONSTRUCTION_QUALITY" : "GENERAL";
  const [isAfterCareEmployeeOrSiteManager, setIsAfterCareEmployeeOrSiteManager] = useState(false);
  const [paginationModel, setPaginationModel] = useState(() => getLocalStorageItem(
    LOCAL_STORAGE_GRID_KEYS.DASHBOARD_PAGINATION[keyPrefix], undefined
  ))
  const [isRowsDataLoading, setIsRowsDataLoading] = useState(false)
  const [isQuestionnaireCreationModalOpen,setIsQuestionnaireCreationModalOpen] = useState(false)
  const [rowSelectionModel, setRowSelectionModel] = useState(INITIAL_ROW_SELECTION_MODEL);
  const apiRef = useGridApiRef();
  const canCreateQuestionnaire = all.some(a => a.roles?.includes(userAccountRoleConstants.siteManager)) && app === apps.constructionQuality;  

  
  useEffect(() => {
    if (
      all.every(
        (a) =>
          !a.roles ||
          a.roles.length === 0 ||
          a.roles.every(
            (b) =>
              b === userAccountRoleConstants.subContractorSupervisor ||
              b === userAccountRoleConstants.subContractorEmployee
          )
      )
    ) {
      if (app === apps.aftercare) {
        history.push("/home")
      } else if (app === apps.constructionQuality) {
        history.push("/kwaliteitsborging");
      }
    } else
      setIsAfterCareEmployeeOrSiteManager(
        all.some(
          (a) =>
            a.roles &&
            (
              a.roles.includes(userAccountRoleConstants.aftercareEmployee) ||
              a.roles.includes(userAccountRoleConstants.siteManager)
            )
        )
      );
  }, [all]);

  useEffect(() => {
    setIsRowsDataLoading(true)
    const projectsGroup = groupBy(all, "projectId");
    const projects = Object.keys(projectsGroup);
    if (!buildingLoading) {
      (async () => {
        try {
          const allProjects = (await getRRDashboardCounts(app, projects)).data ?? [];
          const dashboardRowsData = [];
          for (let i = 0; i < allProjects?.length; i++) {
            const singleProject = allProjects[i];
            const { new: newRepairRequests, projectId, projectNo, projectName, noOfObjects, inProgress, overDue, workOrdersOverdue, type, constructionType, phase, city, workOrdersInProgress } = singleProject || {}

            dashboardRowsData.push({
              id: projectId,
              objects: noOfObjects,
              projectName,
              newRepairRequests: newRepairRequests,
              address: city,
              inProgressRepairRequests: inProgress,
              overdueRepairRequests: overDue,
              openWorkOrders: workOrdersInProgress,
              overdueWorkOrders: workOrdersOverdue,
              projectId,
              type: t(`project.type.${type}`),
              constructionType: t(`project.constructionType.${constructionType}`),
              phase,
              projectNo
            });
          }
          setRowsData(dashboardRowsData)
          setIsRowsDataLoading(false)
        } catch (err) {
          console.error(err);
          setIsRowsDataLoading(false)
        }
      })();
    }
    return () => {
      setRowsData([]);
    };
  }, [history.location.key, all, app, buildingLoading, i18n.language]);

  const rrDbStatuses = useMemo(() => repairRequestDbStatuses(i18n, lng), [i18n?.isInitialized, lng, ready]);

  const getSelectedProjectAndPathname = (projectId, isWorkOrderColumnClicked = false) => {
    const selectedProject = all.filter(
      (x) => x.projectId === projectId
    )[0];
    let pathname = "/nazorg"
    if (app === apps.aftercare) {
      pathname += isWorkOrderColumnClicked ? "/werkbonnen" : "/meldingen";
    } else if (app === apps.constructionQuality) {
      pathname = `/werk/${selectedProject.projectNo}` + (isWorkOrderColumnClicked ? "/werkbonnen" : "/kwaliteitsborging");
    }
    return { selectedProject, pathname }
  }

  const getOptions = getGridContextMenuList({
    filterKey: LOCAL_STORAGE_GRID_KEYS.DASHBOARD_COLUMN_FILTER[keyPrefix],
    sortKey: LOCAL_STORAGE_GRID_KEYS.DASHBOARD_COLUMN_SORT[keyPrefix],
    i18n,
    t,
    apiRef,
    mode: "client"
  })

  const columns = useMemo(() => {
    const baseColumns  = [
      {
        field: "projectNo",
        headerName: t("general.projectNumber"),
        flex: 1,
        minWidth: 90, 
        download: true,
        renderCell: (params) => {
          const rowData = params.row;
          const projectBuildings = all.filter(building => building.projectId === rowData.projectId) 
          const isSiteManagerOrAftercareEmployeeRoleAvailable  = projectBuildings.some(building=>building.roles && (building.roles.includes(userAccountRoleConstants.aftercareEmployee) || building.roles.includes(userAccountRoleConstants.siteManager))) 
          return  <>{isSiteManagerOrAftercareEmployeeRoleAvailable ? <LinkColumnV2 label={rowData.projectNo} to={`/projectbeheer/${rowData.projectId}`} /> : <LabelColumnV2 label={rowData.projectNo} />}</>
        },
      },
      {
        field: "projectName",
        headerName: t("general.projectName"),
        type: "singleSelect",
        valueOptions: rowsData.map(row => row.projectName),
        flex: 1,
        minWidth: 150,
        download: true,
        renderCell: (params) => {
          const rowData = params.row;
          const projectBuildings = all.filter(building => building.projectId === rowData.projectId)
          const isSiteManagerOrAftercareEmployeeRoleAvailable = projectBuildings.some(building => building.roles && (building.roles.includes(userAccountRoleConstants.aftercareEmployee) || building.roles.includes(userAccountRoleConstants.siteManager)))
          const cellSubMenuOptions = {
            options: getOptions({ field: "projectName", isQuickFilterDisplay: Boolean(rowData.projectName) }), extraData: {
              fieldName: "projectName",
              type: "singleSelect"
            }
          }

          return <>{isSiteManagerOrAftercareEmployeeRoleAvailable ? <LinkColumnV2 request={rowData} cellSubMenuOptions={cellSubMenuOptions} label={rowData.projectName} to={`/projectbeheer/${rowData.projectId}`} /> : <LabelColumnV2 label={rowData.projectName} cellSubMenuOptions={cellSubMenuOptions}
          />}</>
        },
      },
      {
        field: "address",
        headerName: t("general.address.city"),
        flex: 1,
        download: true,
        minWidth: 90,
        renderCell: (params) => {
          const rowData = params.row;
          return (
            <LabelColumnV2
              label={rowData.address}
              cellSubMenuOptions={{ options: getOptions({ field: "address", isQuickFilterDisplay: Boolean(rowData.address) }), extraData: { fieldName: "address" } }}
              request={rowData}
            />
          );
        },
      },
      {
        field: "objects",
        type: 'number',
        headerName: t("general.objects"),
        flex: 1,
        minWidth: 90,
        download: true,
        renderCell: (params) => {
          const rowData = params.row;
          return (
            <LabelColumnV2
              label={rowData.objects}
              cellSubMenuOptions={{ options: getOptions({ field: "objects", isQuickFilterDisplay: Boolean(rowData.objects?.toString()) }), extraData: { fieldName: "objects", type : "number" } }}
              request={rowData}
              isNumeric
              />
          );
        },
      },
      {
        field: "newRepairRequests",
        type: 'number',
        headerName: t("site.manager.new.repairrequests"),
        flex: 1,
        minWidth: 90,
        download: true,
        renderCell: (params) => {
          const rowData = params.row;
          const { selectedProject, pathname } = getSelectedProjectAndPathname(rowData.projectId)
          return <LinkColumnV2 
           label={rowData.newRepairRequests}
           request={rowData} 
           to={{ pathname, state: { filter: "newRepairRequests", selectedProject, status: [rrDbStatuses.NEW] } }}
           cellSubMenuOptions={{ options: getOptions({ field: "newRepairRequests", isQuickFilterDisplay: Boolean(rowData.newRepairRequests?.toString()) }),  extraData: { fieldName: "newRepairRequests", type : "number"} }}
           isNumeric
           />
        },
      },
      {
        field: "inProgressRepairRequests",
        type: 'number',
        headerName: t("site.manager.inprogress.repairrequests"),
        flex: 1,
        minWidth: 90,
        download: true,
        renderCell: (params) => {
          const rowData = params.row;
          const { selectedProject, pathname } = getSelectedProjectAndPathname(rowData.projectId)
          return  <LinkColumnV2
          label={rowData.inProgressRepairRequests}
          to={{ pathname, state: { filter: "inProgressRepairRequests", selectedProject, status: [rrDbStatuses.IN_PROGRESS] } }}
          cellSubMenuOptions={{
            options: getOptions({ field: "inProgressRepairRequests", isQuickFilterDisplay: Boolean(rowData.inProgressRepairRequests?.toString()) }),
            extraData: { fieldName: "inProgressRepairRequests", type : "number" }
          }}
          isNumeric
          request={rowData}
        />
        },
      },
      {
        field: "overdueRepairRequests",
        type: 'number',
        headerName: t("site.manager.overdue.repairrequests"),
        flex: 1,
        download: true,
        minWidth: 90,
        hidden: !isAfterCareEmployeeOrSiteManager,
        renderCell: (params) => {
          const rowData = params.row;
          const { selectedProject, pathname } = getSelectedProjectAndPathname(rowData.projectId)
          return <LinkColumnV2 color="error" 
          label={rowData.overdueRepairRequests}
          request={rowData}
           to={{ pathname, state: { filter: "overdueRepairRequests", selectedProject, isOverDue: true } }} 
           cellSubMenuOptions={{ options: getOptions({ field: "overdueRepairRequests", isQuickFilterDisplay: Boolean(rowData.overdueRepairRequests?.toString()) }),  extraData: { fieldName: "overdueRepairRequests", type : "number"} }}
           isNumeric
          />
        },
      },
      {
        field: "openWorkOrders",
        type: 'number',
        headerName: t("site.manager.open.work.orders"),
        flex: 1,
        minWidth: 90,
        download: true,
        hidden: !isAfterCareEmployeeOrSiteManager,
        renderCell: (params) => {
          const rowData = params.row;
          const { selectedProject, pathname } = getSelectedProjectAndPathname(rowData.projectId, true)
          return <LinkColumnV2 label={rowData.openWorkOrders}
           to={{ pathname, state: { filter: "openWorkOrders", selectedProject } }}
           request={rowData}
           cellSubMenuOptions={{
            options: getOptions({ field: "openWorkOrders", isQuickFilterDisplay: Boolean(rowData.openWorkOrders?.toString()) }),
            extraData: { fieldName: "openWorkOrders", type : "number" }
          }}
           isNumeric
          />
        },
      },
      {
        field: "overdueWorkOrders",
        type: 'number',
        headerName: t("site.manager.overdue.work.orders"),
        flex: 1,
        minWidth: 90,
        download: true,
        hidden: !isAfterCareEmployeeOrSiteManager,
        renderCell: (params) => {
          const rowData = params.row;
          const { selectedProject, pathname } = getSelectedProjectAndPathname(rowData.projectId, true)
          return <LinkColumnV2 color="error"
           label={rowData.overdueWorkOrders}
           to={{ pathname, state: { filter: "overdueWorkOrders", selectedProject } }} 
           request={rowData}
            cellSubMenuOptions={{
            options: getOptions({ field: "overdueWorkOrders", isQuickFilterDisplay: Boolean(rowData.overdueWorkOrders?.toString()) }),
            extraData: { fieldName: "overdueWorkOrders", type : "number" }
          }}
           isNumeric
          />
        },
      },
      {
        field: "type",
        headerName: t("general.type"),
        flex: 1,
        minWidth: 100,
        download: true,
        renderCell: (params) => {
          const rowData = params.row;
          return (
            <LabelColumnV2
              label={rowData.type}
              cellSubMenuOptions={{ options: getOptions({ field: "type", isQuickFilterDisplay: Boolean(rowData.address) }), extraData: { fieldName: "type" } }}
              request={rowData}
            />
          );
        },
      },
      {
        field: "constructionType",
        headerName: t("general.project.constructionType"),
        flex: 1,
        minWidth: 90,
        download: true,
        renderCell: (params) => {
          const rowData = params.row;
          return (
            <LabelColumnV2
              label={rowData.constructionType}
              cellSubMenuOptions={{ options: getOptions({ field: "constructionType", isQuickFilterDisplay: Boolean(rowData.constructionType) }), extraData: { fieldName: "constructionType" } }}
              request={rowData}
            />
          );
        },
      },
      {
        field: "phase",
        headerName: t("general.phase"),
        flex: 1,
        minWidth: 90,
        download: true,
        renderCell: (params) => {
          const rowData = params.row;
          return (
            <LabelColumnV2
              label={rowData.phase}
              cellSubMenuOptions={{ options: getOptions({ field: "phase", isQuickFilterDisplay: Boolean(rowData.phase) }), extraData: { fieldName: "phase" } }}
              request={rowData}
            />
          );
        },
      }
    ];
    return baseColumns 
  }, [getSelectedProjectAndPathname, keyPrefix, isAfterCareEmployeeOrSiteManager, t])

 const handleSurveyCreationModalClose = () => {
        setIsQuestionnaireCreationModalOpen(false);
  };

  const projectIds = useMemo(() => {
    return getIdsForGridSelectedRows(rowSelectionModel, rowsData)
  }, [rowSelectionModel, rowsData]);

  const onPaginationModelChange = useCallback((newPaginationModel) => {
    setPaginationModel(newPaginationModel)
    setLocalStorageItem(
      LOCAL_STORAGE_GRID_KEYS.DASHBOARD_PAGINATION[keyPrefix],
      newPaginationModel
    );
  }, [keyPrefix])

  const getToolbarProps = useMemo(() => {
    const hasSelectedRows = projectIds.length > 0 
     const projectsGroup = groupBy(all, "projectId");

    const isCreateQuestionnaireAllowed = projectIds.every((projectId)=>{
      return projectsGroup[projectId]?.some((building)=> building.roles?.includes(userAccountRoleConstants.siteManager))
    })
    const toolbarProps = {
      taskMenuListItems: hasSelectedRows && canCreateQuestionnaire && isCreateQuestionnaireAllowed
      ? [
        {
            title: t("questionnaire.addQuestionnaire"),
            icon: (props) => <Add {...props} />,
            onClick: () => setIsQuestionnaireCreationModalOpen(true),
            closeTaskMenu: true,
          },
        ]
      : [],
    };
    return toolbarProps;
  }, [projectIds, all, canCreateQuestionnaire, t]);

  if (buildingLoading)
    return (
      (<Stack
        sx={{
          height: "100%",
          justifyContent: "center",
          alignItems: "center"
        }}>
        <CircularProgress size={25} />
      </Stack>)
    );

      return (
    <Container maxWidth={false} className={classes.mainContainer}>
      <DataGridV2
        localSearchIdentifier={LOCAL_STORAGE_GRID_KEYS.DASHBOARD_SEARCH_TEXT[keyPrefix]}
        localColumnFilterIdentifier={LOCAL_STORAGE_GRID_KEYS.DASHBOARD_COLUMN_FILTER[keyPrefix]}
        localColumnSortIdentifier={LOCAL_STORAGE_GRID_KEYS.DASHBOARD_COLUMN_SORT[keyPrefix]}
        localColumnOrderIdentifier={LOCAL_STORAGE_GRID_KEYS.DASHBOARD_COLUMN_ORDER[keyPrefix]}
        localColumnWidthIdentifier={LOCAL_STORAGE_GRID_KEYS.DASHBOARD_COLUMN_WIDTH[keyPrefix]}
        localColumnVisibilityIdentifier={LOCAL_STORAGE_GRID_KEYS.DASHBOARD_COLUMN_VISIBILITY[keyPrefix]}
        apiRef={apiRef}
        loading={buildingLoading || isRowsDataLoading}
        checkboxSelection
        rows={rowsData}
        columns={columns}
        pagination
        {...(!!paginationModel && { paginationModel })}
        onPaginationModelChange={onPaginationModelChange}
        toolbarProps={{
          ...getToolbarProps,
          title: t("general.projects"),
          selectedRows: rowSelectionModel
        }}
        filterMode="client"
        onRowSelectionModelChange={(newRowSelectionModel) => {
          setRowSelectionModel(newRowSelectionModel);
        }}
      />
          {isQuestionnaireCreationModalOpen && (
            <QuestionnaireCreationModal
              handleClose={() => handleSurveyCreationModalClose()}
              projectId={projectIds}
            />
          )}
    </Container>
  );
};

const useStyles = makeStyles((theme) => ({
  mainContainer: {
    height: "100%",
    width: "100%",
    overflow: "auto",
    padding: 0,
  },
}));

export { Dashboard };
